import {
    createFeatureReducer,
    EntityState,
    FeatureActionReducer,
    PayloadAction
} from '../../store/store.utils';

import { omit, union } from 'lodash-es';
import { ApiError } from '../../../../common/models/api-response.interface';
import {
    Alert,
    AlertSeverity
} from '../../../../common/models/alert.interface';

export interface AlertState {
    data: EntityState<number, Alert>;
}

export const initialAlertState = {
    data: {
        idMap: {},
        idSort: []
    }
};

abstract class AlertActionReducer extends FeatureActionReducer<AlertState> {
    protected constructor(name: string) {
        super('Alert', name);
    }
}

function generateAlertID(state: AlertState): number {
    let alertCount = state.data.idSort.length;
    return alertCount > 0 ? state.data.idSort[alertCount - 1] + 1 : 0;
}

function addAlertToState(state: AlertState, alert: Alert) {
    return {
        ...state,
        data: {
            idMap: {
                ...state.data.idMap,
                [alert['id']]: alert
            },
            idSort: union(state.data.idSort, [alert['id']])
        }
    };
}
export class AddAlert
    extends AlertActionReducer
    implements PayloadAction<Alert>
{
    constructor(public readonly payload) {
        super('Add Alert');
    }
    reduce(state: AlertState): AlertState {
        let alertCount = state.data.idSort.length;
        let newID = alertCount > 0 ? state.data.idSort[alertCount - 1] + 1 : 0;
        const newPayload = {
            ...this.payload,
            id: newID
        };

        return addAlertToState(state, newPayload);
    }
}

export class AddAPIErrorAlert extends AlertActionReducer {
    constructor(public readonly payload: ApiError) {
        super('Add API Error Alert');
    }
    reduce(state: AlertState): AlertState {
        let alert: Alert = {
            message:
                this.payload?.message ||
                'Something went wrong. Please try again.',
            severity: this.payload?.severity || AlertSeverity.DANGER,
            id: generateAlertID(state)
        };
        return addAlertToState(state, alert);
    }
}

export class RemoveAlert extends AlertActionReducer {
    constructor(public readonly alertID: number) {
        super('Remove Alert');
    }
    reduce(state: AlertState): AlertState {
        const idMap = omit(state.data.idMap, this.alertID);
        return {
            ...state,
            data: {
                idMap,
                idSort: state.data.idSort.filter((id) => id !== this.alertID)
            }
        };
    }
}

const typedReducer = createFeatureReducer(
    AlertActionReducer,
    initialAlertState
);
export function alertReducer(state: any, action: any) {
    return typedReducer(state, action);
}
